import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { COUNTRY_CODES, parsePhoneAndCountry, sanitizePhone } from '@/constants/countryCodes';
import { cn } from '@/lib/utils';

export function PhoneInputWithCountry({
  value = '',
  onChange,
  className,
  selectClassName,
  inputClassName,
  placeholder = '9876543210',
  disabled = false,
  required = false,
  id = 'phone_input',
}) {
  const [countryCode, setCountryCode] = useState(() => {
    const parsed = parsePhoneAndCountry(value);
    return parsed.countryCode || '+91';
  });
  const [localNumber, setLocalNumber] = useState(() => {
    const parsed = parsePhoneAndCountry(value);
    return parsed.localNumber || '';
  });
  const lastEmittedRef = useRef(value);

  // Sync internal state when external E.164 value changes (only if not self-emitted)
  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      lastEmittedRef.current = value;
      if (value) {
        const parsed = parsePhoneAndCountry(value);
        setCountryCode(parsed.countryCode);
        setLocalNumber(parsed.localNumber);
      } else {
        setLocalNumber('');
      }
    }
  }, [value]);

  const handleCountryChange = (e) => {
    const newCode = e.target.value;
    setCountryCode(newCode);
    const cleanDigits = sanitizePhone(localNumber).replace(/\D/g, '');
    const fullE164 = cleanDigits ? `${newCode}${cleanDigits}` : '';
    lastEmittedRef.current = fullE164;
    if (onChange) {
      onChange(fullE164);
    }
  };

  const handleLocalNumberChange = (e) => {
    const rawVal = e.target.value;
    if (rawVal.trim().startsWith('+')) {
      const parsed = parsePhoneAndCountry(rawVal);
      setCountryCode(parsed.countryCode);
      setLocalNumber(parsed.localNumber);
      const cleanDigits = sanitizePhone(parsed.localNumber).replace(/\D/g, '');
      const fullE164 = cleanDigits ? `${parsed.countryCode}${cleanDigits}` : '';
      lastEmittedRef.current = fullE164;
      if (onChange) {
        onChange(fullE164);
      }
      return;
    }
    const cleanDigits = rawVal.replace(/\D/g, '');
    setLocalNumber(rawVal);
    const fullE164 = cleanDigits ? `${countryCode}${cleanDigits}` : '';
    lastEmittedRef.current = fullE164;
    if (onChange) {
      onChange(fullE164);
    }
  };

  // Find the selected country object to show its flag and dial code
  const selectedCountry = COUNTRY_CODES.find((c) => c.code === countryCode) || {
    code: countryCode,
    country: '',
    label: countryCode,
  };
  const flag = selectedCountry.label ? selectedCountry.label.split(' ')[0] : '🌐';

  return (
    <div className={cn("flex gap-2 w-full max-w-full min-w-0 box-border items-stretch", className)}>
      {/* Country Code Trigger: Native select overlay pattern */}
      <div className="relative shrink-0 flex items-center">
        {/* Visible Display Box */}
        <div
          className={cn(
            "h-10 sm:h-11 min-w-[5.25rem] sm:min-w-[5.75rem] px-2.5 sm:px-3 rounded-xl border border-input bg-background text-foreground text-xs sm:text-sm font-semibold flex items-center justify-between gap-1.5 shadow-sm transition-all box-border pointer-events-none select-none",
            disabled && "opacity-50 cursor-not-allowed",
            selectClassName
          )}
        >
          <span className="flex items-center gap-1.5 truncate">
            <span className="text-base leading-none select-none">{flag}</span>
            <span className="font-bold tracking-tight text-foreground text-xs sm:text-sm">{countryCode}</span>
          </span>
          <ChevronDown className="w-3.5 h-3.5 opacity-60 text-muted-foreground shrink-0 ml-0.5" />
        </div>

        {/* Real Native Select on top: invisible (opacity-0) and covers entire trigger box */}
        <select
          value={countryCode}
          onChange={handleCountryChange}
          disabled={disabled}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10 disabled:cursor-not-allowed"
          aria-label="Country Code"
        >
          {COUNTRY_CODES.map((item) => (
            <option key={`${item.country}-${item.code}`} value={item.code} className="text-foreground bg-background">
              {item.label}
            </option>
          ))}
        </select>
      </div>

      {/* Local Phone Number Input */}
      <input
        id={id}
        type="tel"
        value={localNumber}
        onChange={handleLocalNumberChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={cn(
          "flex-1 min-w-0 w-0 h-10 sm:h-11 px-3 sm:px-3.5 rounded-xl border border-input bg-background text-foreground text-xs sm:text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all box-border shadow-sm",
          disabled && "opacity-50 cursor-not-allowed",
          inputClassName
        )}
      />
    </div>
  );
}
